import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSubscription } from "@/context/SubscriptionContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Plus, DotsThreeVertical, UsersThree, CircleNotch, PencilSimple, Trash, Key } from "@phosphor-icons/react";
import { toast } from "sonner";

const MEMBER_ROLES = [
  { value: "admin", label: "Administrador" },
  { value: "accountant", label: "Contabilista" },
  { value: "works_manager", label: "Gestor de obras" },
  { value: "salesperson", label: "Vendedor" },
  { value: "worker", label: "Trabalhador" },
  { value: "client", label: "Cliente" },
];
const LABELS = { owner: "Proprietário", admin: "Administrador", accountant: "Contabilista",
  works_manager: "Gestor de obras", worker: "Trabalhador", salesperson: "Vendedor", client: "Cliente" };

export default function Team() {
  const { user } = useAuth();
  const { entitlement, plans } = useSubscription();
  const canManage = ["owner", "admin"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "worker" });
  const [pwOpen, setPwOpen] = useState(null);
  const [newPw, setNewPw] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/team");
      setItems(data.items);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm({ name: "", email: "", role: "worker" }); setOpen(true); };
  const openEdit = (m) => { setEditing(m); setForm({ name: m.name, email: m.email, role: m.role }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) return toast.error("O nome é obrigatório");
    setBusy(true);
    try {
      if (editing) {
        await api.put(`/team/${editing.id}`, { name: form.name, role: form.role });
        toast.success("Membro atualizado");
      } else {
        if (!form.email.trim()) return toast.error("Email obrigatório");
        const { data } = await api.post("/team/invite", form);
        if (data.email_sent) toast.success("Convite enviado por email");
        else toast.info(`Não foi possível enviar o email. Partilhe este link: ${data.invite_url}`, { duration: 12000 });
      }
      setOpen(false); load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const remove = async (m) => {
    if (!window.confirm(`Eliminar ${m.name}?`)) return;
    try { await api.delete(`/team/${m.id}`); toast.success("Membro eliminado"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const resetPw = async () => {
    if (newPw.length < 6) return toast.error("Mínimo 6 caracteres");
    try {
      await api.post(`/team/${pwOpen.id}/reset-password`, { password: newPw });
      toast.success("Palavra-passe redefinida");
      setPwOpen(null); setNewPw("");
    } catch (err) { toast.error(apiError(err)); }
  };

  const limit = entitlement?.state === "active" && entitlement?.plan
    ? (plans || []).find((p) => p.key === entitlement.plan)?.max_users ?? null : null;

  return (
    <div className="space-y-5" data-testid="team-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Organização</p>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Equipa</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {loading ? "A carregar…" : (
              <>
                {items.length} membro(s){limit ? ` de ${limit} do plano` : ""}
                {entitlement?.state === "trialing" ? " · utilizadores ilimitados durante o teste" : ""}
              </>
            )}
          </p>
        </div>
        {canManage && (
          <Button className="rounded-full" onClick={openNew} data-testid="new-member-btn">
            <Plus size={18} className="mr-1.5" /> Convidar membro
          </Button>
        )}
      </div>

      <div className="rounded-xl border border-border bg-card">
        {loading ? (
          <div className="flex h-40 items-center justify-center"><CircleNotch size={24} className="animate-spin text-primary" /></div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead className="hidden md:table-cell">E-mail</TableHead>
                <TableHead>Perfil</TableHead>
                {canManage && <TableHead className="text-right">Ações</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((m) => (
                <TableRow key={m.id} className="stagger-item" data-testid={`member-row-${m.id}`}>
                  <TableCell className="font-medium">{m.name} {m.id === user?.id && <span className="text-xs text-muted-foreground">(você)</span>}</TableCell>
                  <TableCell className="hidden md:table-cell text-muted-foreground">{m.email}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={m.role === "owner" ? "border-primary/30 bg-accent text-primary" : ""}>
                      {LABELS[m.role] || m.role}
                    </Badge>
                    {m.status === "invited" && (
                      <Badge variant="outline" className="ml-1 border-amber-200 bg-amber-50 text-amber-700">Convidado</Badge>
                    )}
                  </TableCell>
                  {canManage && (
                    <TableCell className="text-right">
                      {m.role !== "owner" && m.id !== user?.id ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`member-actions-${m.id}`}>
                              <DotsThreeVertical size={20} weight="bold" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEdit(m)}><PencilSimple size={15} className="mr-2" /> Editar</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => { setPwOpen(m); setNewPw(""); }}><Key size={15} className="mr-2" /> Redefinir palavra-passe</DropdownMenuItem>
                            <DropdownMenuItem className="text-destructive" onClick={() => remove(m)}><Trash size={15} className="mr-2" /> Eliminar</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="member-dialog">
          <DialogHeader><DialogTitle>{editing ? "Editar membro" : "Convidar membro"}</DialogTitle>
            <DialogDescription>{editing ? "Atualize o nome e o perfil do membro." : "Enviaremos um email com um link para o membro definir a própria palavra-passe."}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Nome *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="member-name" />
            </div>
            <div className="space-y-1.5">
              <Label>E-mail *</Label>
              <Input type="email" value={form.email} disabled={!!editing}
                onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="member-email" />
            </div>
            <div className="space-y-1.5">
              <Label>Perfil</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="member-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MEMBER_ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={busy} data-testid="save-member-btn">
              {busy ? <CircleNotch className="animate-spin" /> : (editing ? "Guardar" : "Enviar convite")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pwOpen} onOpenChange={(o) => !o && setPwOpen(null)}>
        <DialogContent className="max-w-sm" data-testid="reset-pw-dialog">
          <DialogHeader><DialogTitle>Redefinir palavra-passe</DialogTitle>
            <DialogDescription>Defina uma nova palavra-passe temporária para o membro.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label>Nova palavra-passe para {pwOpen?.name}</Label>
            <Input type="text" value={newPw} onChange={(e) => setNewPw(e.target.value)}
              placeholder="Mínimo 6 caracteres" data-testid="reset-pw-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwOpen(null)}>Cancelar</Button>
            <Button onClick={resetPw} data-testid="save-reset-pw-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
